import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useHostingStore, ServerType } from '../../store/hostingStore';
import { X, Server, Loader2, ChevronDown, Crown, Check, Search, MapPin, Egg } from 'lucide-react';

interface Props {
  onClose: () => void;
}

const SERVER_TYPES: { id: ServerType; label: string; description: string; color: string }[] = [
  { id: 'paper',   label: 'Paper',   description: 'Optimisé · Plugins Bukkit/Spigot', color: 'text-red-400 border-red-500/30 bg-red-500/10' },
  { id: 'vanilla', label: 'Vanilla', description: 'Minecraft officiel',               color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
  { id: 'fabric',  label: 'Fabric',  description: 'Mods légers · Performant',         color: 'text-blue-400 border-blue-500/30 bg-blue-500/10' },
  { id: 'forge',   label: 'Forge',   description: 'Écosystème mods classique',         color: 'text-orange-400 border-orange-500/30 bg-orange-500/10' },
  { id: 'spigot',  label: 'Spigot',  description: 'Plugins Bukkit/Spigot',             color: 'text-amber-400 border-amber-500/30 bg-amber-500/10' },
  { id: 'purpur',  label: 'Purpur',  description: 'Fork Paper · Plus de config',       color: 'text-purple-400 border-purple-500/30 bg-purple-500/10' },
];

type McVersionEntry = { id: string; type: string; releaseTime: string };

const CreateServerModal: React.FC<Props> = ({ onClose }) => {
  const { plans, eggs, locations, createServer, fetchPlans, fetchEggs, fetchLocations } = useHostingStore();

  // Form state
  const [name, setName] = useState('');
  const [serverType, setServerType] = useState<ServerType>('paper');
  const [mcVersion, setMcVersion] = useState('1.21.4');
  const [selectedPlan, setSelectedPlan] = useState('plan-free');
  const [description, setDescription] = useState('');
  const [selectedEggId, setSelectedEggId] = useState<number | undefined>(undefined);
  const [selectedLocationId, setSelectedLocationId] = useState<number | undefined>(undefined);
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // MC Versions
  const [mcVersions, setMcVersions] = useState<McVersionEntry[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(true);
  const [versionDropdownOpen, setVersionDropdownOpen] = useState(false);
  const [versionSearch, setVersionSearch] = useState('');
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (!plans.length) fetchPlans();
    if (!eggs.length) fetchEggs();
    if (!locations.length) fetchLocations();
  }, []);

  // Load MC versions from IPC or hard-code a few common ones
  useEffect(() => {
    const api = (window as any).api;
    if (api?.getMinecraftVersions) {
      api.getMinecraftVersions()
        .then((data: McVersionEntry[]) => setMcVersions(Array.isArray(data) ? data.filter(v => v.type === 'release') : []))
        .catch(() => {})
        .finally(() => setLoadingVersions(false));
    } else {
      // Fallback: common versions
      setMcVersions([
        { id: '1.21.4', type: 'release', releaseTime: '' },
        { id: '1.21.3', type: 'release', releaseTime: '' },
        { id: '1.21.2', type: 'release', releaseTime: '' },
        { id: '1.21.1', type: 'release', releaseTime: '' },
        { id: '1.21', type: 'release', releaseTime: '' },
        { id: '1.20.6', type: 'release', releaseTime: '' },
        { id: '1.20.4', type: 'release', releaseTime: '' },
        { id: '1.20.2', type: 'release', releaseTime: '' },
        { id: '1.20.1', type: 'release', releaseTime: '' },
        { id: '1.19.4', type: 'release', releaseTime: '' },
        { id: '1.18.2', type: 'release', releaseTime: '' },
        { id: '1.16.5', type: 'release', releaseTime: '' },
        { id: '1.12.2', type: 'release', releaseTime: '' },
      ]);
      setLoadingVersions(false);
    }
  }, []);

  // Position dropdown
  useEffect(() => {
    if (versionDropdownOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, [versionDropdownOpen]);

  // Click-outside for dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setVersionDropdownOpen(false);
      }
    };
    if (versionDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [versionDropdownOpen]);

  const filteredVersions = mcVersions.filter((v) =>
    !versionSearch || v.id.includes(versionSearch),
  );

  const handleCreate = async () => {
    if (!name.trim()) return;
    setIsCreating(true);
    try {
      const id = await createServer({
        name: name.trim(),
        serverType,
        mcVersion,
        planId: selectedPlan,
        description: description.trim() || undefined,
        eggId: selectedEggId,
        locationId: selectedLocationId,
        eulaAccepted,
      });
      if (id) onClose();
    } finally {
      setIsCreating(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-bg-secondary border border-border-color rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-color">
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-cyan-400" />
            <h2 className="text-base font-bold text-text-primary">Nouveau serveur Minecraft</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-bg-tertiary text-text-muted hover:text-text-primary transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5 max-h-[60vh] overflow-y-auto gaming-scrollbar">
          {/* Name */}
          <div>
            <label className="text-xs text-text-muted block mb-1.5">Nom du serveur</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Mon serveur survival"
              autoFocus
              maxLength={100}
              className="w-full px-3 py-2.5 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:border-cyan-500 outline-none transition-colors"
            />
          </div>

          {/* Server type */}
          <div>
            <label className="text-xs text-text-muted block mb-1.5">Type de serveur</label>
            <div className="grid grid-cols-3 gap-2">
              {SERVER_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setServerType(type.id)}
                  className={`flex flex-col items-center px-2 py-2.5 rounded-lg border text-center transition-all ${
                    serverType === type.id
                      ? `${type.color} ring-1 ring-current`
                      : 'border-border-color bg-bg-primary hover:border-text-muted/30'
                  }`}
                >
                  <span className="text-xs font-semibold">{type.label}</span>
                  <span className="text-[9px] text-text-muted mt-0.5 leading-tight">{type.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* MC Version */}
          <div>
            <label className="text-xs text-text-muted block mb-1.5">Version Minecraft</label>
            <div className="relative">
              <button
                ref={triggerRef}
                type="button"
                onClick={() => setVersionDropdownOpen((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2.5 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary hover:border-cyan-500/50 focus:border-cyan-500 outline-none transition-colors"
              >
                <span>{mcVersion}</span>
                {loadingVersions ? (
                  <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
                ) : (
                  <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${versionDropdownOpen ? 'rotate-180' : ''}`} />
                )}
              </button>

              {versionDropdownOpen && dropdownPos && ReactDOM.createPortal(
                <div
                  ref={dropdownRef}
                  style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
                  className="fixed z-[300] bg-bg-secondary border border-border-color rounded-lg shadow-xl"
                >
                  {/* Search */}
                  <div className="px-2 py-1.5 border-b border-border-color">
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-bg-primary rounded-md">
                      <Search className="w-3.5 h-3.5 text-text-muted" />
                      <input
                        type="text"
                        value={versionSearch}
                        onChange={(e) => setVersionSearch(e.target.value)}
                        placeholder="Rechercher..."
                        className="flex-1 bg-transparent text-xs text-text-primary outline-none placeholder:text-text-muted"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-40 overflow-y-auto gaming-scrollbar">
                    {filteredVersions.map((v) => (
                      <button
                        key={v.id}
                        onClick={() => { setMcVersion(v.id); setVersionDropdownOpen(false); }}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-bg-tertiary transition-colors flex items-center justify-between ${
                          v.id === mcVersion ? 'text-cyan-400' : 'text-text-primary'
                        }`}
                      >
                        {v.id}
                        {v.id === mcVersion && <Check className="w-3 h-3" />}
                      </button>
                    ))}
                  </div>
                </div>,
                document.body,
              )}
            </div>
          </div>

          {/* Egg selector (dynamic from Pterodactyl) */}
          {eggs.length > 0 && (
            <div>
              <label className="text-xs text-text-muted block mb-1.5">
                <span className="flex items-center gap-1"><Egg className="w-3 h-3" /> Egg Pterodactyl</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {eggs.map((egg) => (
                  <button
                    key={egg.id}
                    onClick={() => setSelectedEggId(egg.id)}
                    className={`flex flex-col items-start px-3 py-2 rounded-lg border text-left transition-all ${
                      selectedEggId === egg.id
                        ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-300 ring-1 ring-cyan-500/30'
                        : 'border-border-color bg-bg-primary hover:border-text-muted/30 text-text-primary'
                    }`}
                  >
                    <span className="text-xs font-semibold">{egg.name}</span>
                    {egg.description && (
                      <span className="text-[9px] text-text-muted mt-0.5 leading-tight line-clamp-2">{egg.description}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Location selector (dynamic from Pterodactyl) */}
          {locations.length > 0 && (
            <div>
              <label className="text-xs text-text-muted block mb-1.5">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Localisation</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {locations.map((loc) => (
                  <button
                    key={loc.id}
                    onClick={() => setSelectedLocationId(loc.id)}
                    className={`flex flex-col items-start px-3 py-2.5 rounded-lg border text-left transition-all ${
                      selectedLocationId === loc.id
                        ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-300 ring-1 ring-cyan-500/30'
                        : 'border-border-color bg-bg-primary hover:border-text-muted/30 text-text-primary'
                    }`}
                  >
                    <span className="text-xs font-semibold">{loc.long || loc.short}</span>
                    {loc.long && loc.short !== loc.long && (
                      <span className="text-[9px] text-text-muted mt-0.5">{loc.short}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Plan selector */}
          {plans.length > 0 && (
            <div>
              <label className="text-xs text-text-muted block mb-1.5">Plan</label>
              <div className="grid grid-cols-2 gap-2">
                {plans.map((plan) => (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative flex flex-col p-3 rounded-lg border transition-all ${
                      selectedPlan === plan.id
                        ? 'border-cyan-500/50 bg-cyan-500/5 ring-1 ring-cyan-500/30'
                        : 'border-border-color bg-bg-primary hover:border-text-muted/30'
                    }`}
                  >
                    {plan.name === 'plus' && (
                      <Crown className="absolute top-2 right-2 w-3.5 h-3.5 text-amber-400" />
                    )}
                    <span className="text-sm font-bold text-text-primary">{plan.display_name}</span>
                    <div className="text-[10px] text-text-muted mt-1 space-y-0.5">
                      <div>{plan.memory_mb} MB RAM</div>
                      <div>{(plan.disk_mb / 1024).toFixed(0)} GB Disque</div>
                      <div>{plan.max_backups} backups</div>
                    </div>
                    {plan.price_monthly === 0 && (
                      <span className="mt-1.5 text-[10px] font-semibold text-emerald-400">Gratuit</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* EULA Minecraft */}
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={eulaAccepted}
                onChange={(e) => setEulaAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-border-color bg-bg-primary accent-cyan-500 cursor-pointer"
              />
              <div>
                <span className="text-xs font-medium text-text-primary block">
                  J'accepte l'EULA de Minecraft
                </span>
                <span className="text-[10px] text-text-muted mt-0.5 block leading-relaxed">
                  En cochant cette case, vous acceptez le{' '}
                  <a
                    href="https://aka.ms/MinecraftEULA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-400 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    contrat de licence utilisateur final de Mojang
                  </a>.
                  Sans acceptation, le serveur ne pourra pas démarrer.
                </span>
              </div>
            </label>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs text-text-muted block mb-1.5">Description <span className="text-text-muted/50">(optionnelle)</span></label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Un petit serveur survival entre amis..."
              rows={2}
              maxLength={500}
              className="w-full px-3 py-2.5 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:border-cyan-500 outline-none transition-colors resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border-color flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim() || isCreating}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isCreating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Server className="w-4 h-4" />
            )}
            Créer le serveur
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CreateServerModal;
